/**
 * @fileoverview Pure adapter mapping the legacy `documentConfig` (15-section builder model)
 * onto the canonical `DocumentLayout` band/flow model (G-COMPAT).
 * @module document-render/documentConfigToLayout
 *
 * Responsibilities: - Translate an existing template's documentConfig into a DocumentLayout so
 *                     existing templates keep generating once the shared renderer is adopted.
 * Dependencies:     - ./documentLayout (model types).
 * Architecture:     - Pure function, no I/O. Each enabled section becomes a Band; header/footer
 *                     map to the layout's repeating bands; bodyText paragraphs become text
 *                     elements; dynamic sections (violations/photos/metadata/advanced) carry a
 *                     binding so the renderer expands them.
 * Notes:            - The renderer interprets band.config (the carried-through legacy config);
 *                     this adapter does not lose information.
 *                   - A2 additions: page settings (margins/border/watermark) mapping from the
 *                     DocumentConfig.page field, bodyText paragraph conditions, and
 *                     pageBreakBefore on sections.
 */

import {
  type DocumentLayout,
  type DocumentType,
  type Band,
  type BandKind,
  type PageSetup,
  type Element,
  type RenderCondition,
  DOCUMENT_LAYOUT_SCHEMA_VERSION,
} from './documentLayout';

/** Loosely-typed legacy config (the builder's documentConfig). */
export interface LegacyDocumentConfig {
  documentType?: string;
  layoutMode?: string;
  pageSize?: 'letter' | 'a4' | 'legal';
  orientation?: 'portrait' | 'landscape';
  /** Page settings — margins, border, watermark (from DocumentConfig.page). */
  page?: {
    margins?: { top?: number; right?: number; bottom?: number; left?: number };
    border?: { width: number; style: 'solid' | 'double' | 'dashed' | 'dotted'; color: string; radius?: number } | null;
    watermark?: { text: string; opacity: number; angle: number } | null;
  };
  draftWatermark?: boolean;
  sections?: Array<{ id: string; enabled?: boolean; pageBreakBefore?: boolean; [k: string]: unknown }>;
  header?: Record<string, unknown>;
  footerConfig?: Record<string, unknown>;
  bodyText?: { paragraphs?: Array<{ id: string; content: string; condition?: string; isHtml?: boolean }> };
  [k: string]: unknown;
}

const VALID_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'inspectionReport', 'permit', 'noticeOfViolation', 'letter', 'certificateOfCompliance', 'custom',
]);

/** Validate an arbitrary documentType against the model; unknown values fall back to 'custom'. */
function toDocumentType(value: unknown): DocumentType {
  return typeof value === 'string' && VALID_DOCUMENT_TYPES.has(value as DocumentType)
    ? (value as DocumentType)
    : 'custom';
}

const SECTION_KIND: Record<string, BandKind> = {
  cover: 'coverPage',
  recipientBlock: 'recipientBlock',
  subjectLine: 'subjectLine',
  salutation: 'salutation',
  bodyText: 'richText',
  metadata: 'metadataGrid',
  violations: 'violationsList',
  photos: 'photoGallery',
  comments: 'callout',
  contactBlock: 'callout',
  signatures: 'signatureBlock',
  legal: 'legal',
  staticContent: 'richText',
};

/** Per-section config key on the legacy documentConfig (where it differs from the section id). */
const SECTION_CONFIG_KEY: Record<string, string> = {
  violations: 'violationConfig',
  photos: 'photoConfig',
  legal: 'legalText',
};

const ONE_INCH_PT = 72;

function pageSetup(config: LegacyDocumentConfig): PageSetup {
  const p = config.page;
  const m = p?.margins;
  return {
    size: config.pageSize ?? 'letter',
    orientation: config.orientation ?? 'portrait',
    margins: {
      top: m?.top ?? ONE_INCH_PT,
      right: m?.right ?? ONE_INCH_PT,
      bottom: m?.bottom ?? ONE_INCH_PT,
      left: m?.left ?? ONE_INCH_PT,
    },
    border: p?.border ?? null,
    watermark: config.draftWatermark
      ? { text: 'DRAFT', opacity: 0.08, angle: -45 }
      : (p?.watermark ?? null),
  };
}

function toCondition(value: string | undefined): RenderCondition | undefined {
  if (value === 'hasViolations' || value === 'noViolations') return value;
  if (value === 'always') return 'always';
  return undefined;
}

function bodyTextElements(config: LegacyDocumentConfig): Element[] {
  const paragraphs = config.bodyText?.paragraphs ?? [];
  return paragraphs.map((p) => ({
    id: p.id,
    type: 'text' as const,
    html: p.content,
    binding: undefined,
  }));
}

function bandFor(sectionId: string, config: LegacyDocumentConfig, enabled: boolean, pageBreakBefore?: boolean): Band | null {
  const kind = SECTION_KIND[sectionId];
  if (!kind) return null;
  const cfgKey = SECTION_CONFIG_KEY[sectionId] ?? sectionId;
  const sectionConfig = (config as Record<string, unknown>)[cfgKey];

  const band: Band = {
    id: sectionId,
    kind,
    enabled,
    elements: sectionId === 'bodyText' ? bodyTextElements(config) : [],
    config: (sectionConfig && typeof sectionConfig === 'object') ? (sectionConfig as Record<string, unknown>) : undefined,
  };

  // Dynamic sections carry a binding so the renderer expands them.
  if (kind === 'violationsList') {
    const sc = sectionConfig as Record<string, any> | undefined;
    band.elements = [{
      id: `${sectionId}-data`,
      type: 'field',
      binding: { source: 'violations', displayMode: sc?.displayFormat, groupBy: sc?.groupBy, emptyBehavior: 'showEmptyMessage' },
    }];
  } else if (kind === 'photoGallery') {
    band.elements = [{ id: `${sectionId}-data`, type: 'field', binding: { source: 'photos', emptyBehavior: 'hide' } }];
  } else if (kind === 'metadataGrid') {
    band.elements = [{ id: `${sectionId}-data`, type: 'field', binding: { source: 'metadata' } }];
  }

  // Insert a page break band before this band if configured (A2).
  return band;
}

/**
 * @process documentConfigToLayout
 * Steps:    1. Build PageSetup (including page margins, border, watermark from config.page).
 *           2. Map header → layout.header, footer → layout.footer.
 *           3. For each section (in order), create a Band; bodyText paragraphs → text elements;
 *              dynamic sections get a binding. pageBreakBefore sections get a preceding pageBreak band.
 *           4. Return the DocumentLayout.
 * Inputs:   legacy `documentConfig`.
 * Outputs:  DocumentLayout (schemaVersion stamped).
 * Idempotency: REQUIRED — pure mapping, deterministic.
 */
export function documentConfigToLayout(config: LegacyDocumentConfig): DocumentLayout {
  const sections = config.sections ?? [];
  const body: Band[] = [];
  let header: Band | null = null;
  let footer: Band | null = null;

  for (const section of sections) {
    const enabled = section.enabled !== false;
    if (section.id === 'header') {
      header = {
        id: 'header',
        kind: 'letterhead',
        enabled,
        elements: [],
        config: config.header,
      };
      continue;
    }
    if (section.id === 'footer') {
      footer = {
        id: 'footer',
        kind: 'footer',
        enabled,
        elements: [],
        config: config.footerConfig,
      };
      continue;
    }

    // Insert a page break band before this section if configured (A2).
    if (section.pageBreakBefore && enabled) {
      body.push({
        id: `${section.id}-pb`,
        kind: 'pageBreak',
        enabled: true,
        elements: [],
      });
    }

    const band = bandFor(section.id, config, enabled, section.pageBreakBefore);
    if (band) body.push(band);
  }

  return {
    schemaVersion: DOCUMENT_LAYOUT_SCHEMA_VERSION,
    documentType: toDocumentType(config.documentType),
    page: pageSetup(config),
    header,
    footer,
    body,
  };
}
